import { FC, useEffect, useState } from "react";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { clsxMerge } from "shared/lib/helpers/styles";

import { SelectDays } from "./select-days";
import { SelectTime } from "./select-time";
import {
  defaultTimezoneAbbr,
  SCHEDULER_TIMEZONES,
  SelectTimezone,
} from "./select-timezone";

import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import {
  selectorDateToFollowupSetting,
  selectorEmailsSequenceDaysDiff,
  selectorFollowupSettingToDate,
} from "@/modules/email-templates-sidebar/selectors";
import { DayJs } from "shared/lib/helpers/date";

export const FollowupScheduler: FC<PropsWithClassNameI> = ({ className }) => {
  const { activeTemplateId, activeEmailSequenceItemId, getDraft, setDraft } =
    useEmailTemplatesContext();

  const [selectedDays, setSelectedDays] = useState("7");
  const [selectedTime, setSelectedTime] = useState("9:00 AM");
  const [selectedTimezone, setSelectedTimezone] = useState("");

  const handleDaysChange = (value: string) => {
    setSelectedDays(value);
  };

  const handleTimeChange = (value: string) => {
    setSelectedTime(value);
  };

  const handleTimezoneChange = (value: string) => {
    setSelectedTimezone(value);
  };

  /**
   * Recalculate days for each email in the sequence
   * when we change the followup settings
   */
  useEffect(() => {
    const draft = getDraft(activeTemplateId as string);
    const emails = draft?.emails;
    const emailIndex =
      emails?.findIndex(
        (email) => email?.email_id === activeEmailSequenceItemId
      ) || 0;

    const emailsDaysDiff = selectorEmailsSequenceDaysDiff(emails);

    if (draft && selectedTimezone) {
      setDraft(activeTemplateId as string, {
        ...draft,
        emails: draft?.emails?.reduce(
          (emails, email, i) => {
            const prevDate = i > 1 ? emails?.[i - 1]?.scheduled_at : undefined;

            if (email?.email_id === activeEmailSequenceItemId) {
              emails.push({
                ...email,
                scheduled_at: selectorFollowupSettingToDate(
                  selectedDays,
                  selectedTime,
                  selectedTimezone,
                  prevDate
                ),
              });
            } else if (i > emailIndex) {
              /**
               * Recalculate days for each email in the sequence
               */
              emails.push({
                ...email,
                scheduled_at: DayJs(prevDate)
                  .add(emailsDaysDiff?.[i] || 0, "days")
                  .format(),
              });
            } else {
              emails.push(email);
            }

            return emails;
          },
          [] as typeof draft.emails
        ),
      });
    }
  }, [selectedDays, selectedTime, selectedTimezone]);

  useEffect(() => {
    const draft = getDraft(activeTemplateId as string);
    const email = draft?.emails?.find(
      (email) => email?.email_id === activeEmailSequenceItemId
    );
    const emailIndex = draft?.emails?.findIndex(
      (email) => email?.email_id === activeEmailSequenceItemId
    );

    if (email?.scheduled_at && email?.scheduled_at !== "0001-01-01T00:00:00Z") {
      const prevDate =
        emailIndex && emailIndex > 1
          ? draft?.emails?.[emailIndex - 1]?.scheduled_at
          : undefined;

      const { days, time, timezone } = selectorDateToFollowupSetting(
        email?.scheduled_at,
        prevDate
      );

      setSelectedDays(days);
      setSelectedTime(time);
      setSelectedTimezone(
        SCHEDULER_TIMEZONES.find((tz) => tz.tzCode === (timezone as string))
          ?.abbr || defaultTimezoneAbbr
      );
    }
  }, [activeEmailSequenceItemId]);

  return (
    <div className={clsxMerge(className)}>
      <h6 className="mb-2 typography-body-4-medium">Follow-up scheduled</h6>

      <div className="flex items-center gap-2">
        <div>
          <SelectDays value={selectedDays} onChange={handleDaysChange} />
        </div>
        <div>
          <span>days after the last email at</span>
        </div>
        <div className="flex items-center gap-2">
          <SelectTime value={selectedTime} onChange={handleTimeChange} />
          <SelectTimezone
            value={selectedTimezone}
            onChange={handleTimezoneChange}
          />
        </div>
      </div>
    </div>
  );
};
